import { type ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { insertIf } from '@/helpers/general-helpers'
import { ReactComponent as ApprovalIcon } from '@/assets/images/menu/approval-icon.svg'
import { ReactComponent as DashboardIcon } from '@/assets/images/menu/dashboard-icon.svg'
import { ReactComponent as EmployeesIcon } from '@/assets/images/menu/employees-icon.svg'
import { ReactComponent as DocumentsIcon } from '@/assets/images/menu/documents-icon.svg'
import { ReactComponent as EquipmentIcon } from '@/assets/images/menu/equipment-icon.svg'
import { ReactComponent as CalendarIcon } from '@/assets/images/menu/calendar-icon.svg'
import { ReactComponent as CompetenceIcon } from '@/assets/images/menu/competence-icon.svg'
import { ReactComponent as ProcessesIcon } from '@/assets/images/menu/processes-icon.svg'
import { ReactComponent as EngagementIcon } from '@/assets/images/menu/engagement-icon.svg'
import { ReactComponent as PulseIcon } from '@/assets/images/menu/pulse-icon.svg'
import { ReactComponent as StopwatchIcon } from '@/assets/images/icons/time/clock-stopwatch.svg'
import { ReactComponent as SettingsIcon } from '@/assets/images/menu/settings-icon.svg'
import { ReactComponent as InformationIcon } from '@/assets/images/menu/information-icon.svg'
import { ReactComponent as RecruitIcon } from '@/assets/images/menu/recruit-icon.svg'
import { CSSProperties } from 'styled-components'
import type { Permission } from '@/types/Permission'
import useLoggedInUser from './useLoggedInUser'
import useCompanySetting from './useCompanySetting'
import usePermissionCheck from './usePermissionCheck'
import { useQuery } from '@apollo/client'
import { DASHBOARD_VIEW_COUNT } from '@/graphql/queries/Dashboard'

type MenuItem = {
  path: string
  title: string
  icon: ReactNode
  permissions?: Permission[]
  subMenuItems?: SubMenuItem[]
}

export type SubRoute = {
  label: string
  onClick: () => void
}

export interface SubMenuItem {
  to: string
  label: string
  permissions?: Permission[]
  notify?: string | undefined
  style?: CSSProperties | undefined
  subRoutes?: SubRoute[]
  subRoutesDefaultOpen?: boolean
  toggleDefaultOpen?: () => void
}

export default function useMenuItems() {
  const { t } = useTranslation()

  const { company } = useLoggedInUser(true)

  const { hasPermissions } = usePermissionCheck()

  const { data: dashboardCount } = useQuery(DASHBOARD_VIEW_COUNT, {
    skip: hasPermissions(['dashboard:general:write:all']),
  })

  const [timeRegistrationEnabled] = useCompanySetting(
    'ENABLE_TIME_REGISTRATION',
  )

  return useMemo(() => {
    const hasNativeAppAccess = !!company?.nativeAppAccess

    const canViewTimeRegistrationPage = !!timeRegistrationEnabled

    const dashboardTotalCount = dashboardCount?.dashboardViews?.totalCount ?? 0

    const canViewDashboardPage =
      dashboardTotalCount > 0 || hasPermissions(['dashboard:general:write:all'])

    return [
      ...insertIf(!!canViewDashboardPage, {
        path: '/dashboard/',
        title: t('Dashboard'),
        icon: <DashboardIcon />,
      }),
      {
        path: '/employees/',
        title: t('Employees'),
        icon: <EmployeesIcon />,
        subMenuItems: [
          {
            to: '/*',
            label: t('All employees'),
          },
          {
            to: 'communication-log',
            label: t('Communication log'),
            permissions: [
              'log:communication:read:all',
              'log:communication:read:department',
            ],
          },
          {
            to: 'login-log',
            label: t('Login log'),
            permissions: ['log:login:read:all'],
          },
          {
            to: 'deletion-log',
            label: t('Deletion log'),
            permissions: ['log:general:read:all'],
          },
        ],
      },
      {
        path: '/approvals/',
        title: t('Approvals'),
        permissions: [
          'approve:field_value:write:self',
          'approve:field_value:write:department',
          'approve:field_value:write:all',
          'approve:field_value:write:relation',
          'approve:vacation:write:self',
          'approve:vacation:write:department',
          'approve:vacation:write:all',
          'approve:vacation:write:relation',
        ],
        icon: <ApprovalIcon />,
      },
      {
        path: '/documents/',
        title: t('Documents'),
        permissions: [
          'document_folder:department:read:department',
          'document_folder:department:read:all',
          'document_folder:company:read:all',
          'document_folder:blueprint:read:all',
          'document_folder:blueprint:write:all',
          'document:document_type:read:all',
          'document:document_type:write:all',
          'document:template:read:self',
          'document:template:read:department',
          'document:template:read:all',
        ],
        icon: <DocumentsIcon />,
        subMenuItems: [
          {
            to: 'companydocuments/*',
            label: t('Company documents'),
            permissions: [
              'document_folder:department:read:department',
              'document_folder:department:read:all',
              'document_folder:company:read:all',
            ],
          },
          {
            to: 'alldocuments',
            label: t('All documents'),
            permissions: [
              'document_folder:department:read:department',
              'document_folder:department:read:all',
              'document_folder:company:read:all',
            ],
          },
          {
            to: 'structure',
            label: t('Folder structure - employees'),
            permissions: [
              'document_folder:blueprint:read:all',
              'document_folder:blueprint:write:all',
            ],
          },
          {
            to: 'types',
            label: t('Document types'),
            permissions: [
              'document:document_type:read:all',
              'document:document_type:write:all',
            ],
          },
          {
            to: 'templates',
            label: t('Document templates'),
            permissions: [
              'document:template:read:self',
              'document:template:read:department',
              'document:template:read:all',
            ],
          },
        ],
      },
      {
        path: '/equipment/',
        title: t('Equipment'),
        permissions: [
          'equipment:general:read:all',
          'equipment:general:read:department',
          'equipment:bytype:read:all',
          'equipment:bytype:read:department',
        ],
        icon: <EquipmentIcon />,
      },
      {
        path: '/vacation/',
        title: t('Vacation & absence'),
        icon: <CalendarIcon />,
        permissions: [
          'vacation:type:read:all',
          'vacation:assign:read:department',
          'vacation:assign:read:all',
          'vacation:assign_bytype:read:department',
          'vacation:assign_bytype:read:all',
        ],
      },
      {
        path: '/competences/',
        title: t('Competences'),
        icon: <CompetenceIcon />,
        permissions: [
          'competence:general:read:all',
          'competence:assign:read:department',
          'competence:assign:read:all',
        ],
        subMenuItems: [
          {
            to: 'setup',
            label: t('Setup'),
            permissions: ['competence:general:read:all'],
          },
          {
            to: 'assigned',
            label: t('Assigned competences'),
            permissions: [
              'competence:assign:read:department',
              'competence:assign:read:all',
            ],
          },
        ],
      },
      {
        path: '/processes/',
        title: t('Processes'),
        permissions: [
          'process:general:read:department',
          'process:general:read:all',
          'process:template:read:department',
          'process:template:read:all',
          'process:bytype:read:department',
          'process:bytype:read:all',
          'process:process_type:write:all',
        ],
        icon: <ProcessesIcon />,
      },
      {
        path: '/engagementbuilder/',
        title: t('Engagement Builder'),
        permissions: [
          'app_builder:general:read:department',
          'app_builder:general:read:all',
          'app_builder:templates:read:department',
          'app_builder:templates:read:all',
          'app_builder:bytype:read:department',
          'app_builder:bytype:read:all',
          'app_builder:templates_bytype:read:department',
          'app_builder:templates_bytype:read:all',
        ],
        icon: <EngagementIcon />,
      },
      ...insertIf(!!hasNativeAppAccess, {
        path: '/pulse/',
        title: t('Pulse'),
        icon: <PulseIcon />,
        subMenuItems: [
          {
            to: 'surveys',
            label: t('All pulse surveys'),
            permissions: ['pulse:general:read:all', 'pulse:bytype:read:all'],
          },
          {
            to: 'types/*',
            label: t('Types'),
            permissions: ['pulse:type:write:all'],
          },
        ],
        permissions: [
          'pulse:general:read:all',
          'pulse:bytype:read:all',
          'pulse:type:write:all',
        ],
      }),
      ...insertIf(!!canViewTimeRegistrationPage, {
        path: '/time-registration/',
        title: t('Time Registration'),
        icon: <StopwatchIcon />,
        permissions: [
          'time_registration:general:read:department',
          'time_registration:general:read:all',
          'time_registration:break_type:write:all',
          'time_registration:presence_type:write:all',
          'time_registration:project:write:all',
          'time_registration:template:write:all',
        ],
        subMenuItems: [
          {
            to: 'tracking',
            label: t('Overview'),
            permissions: [
              'time_registration:general:read:department',
              'time_registration:general:read:all',
            ],
          },
          {
            to: 'projects',
            label: t('Projects'),
            permissions: ['time_registration:project:write:all'],
          },
          {
            to: 'break-types',
            label: t('Break types'),
            permissions: ['time_registration:break_type:write:all'],
          },
          {
            to: 'presence-types',
            label: t('Presence types'),
            permissions: ['time_registration:presence_type:write:all'],
          },
          {
            to: 'templates',
            label: t('Templates'),
            permissions: ['time_registration:template:write:all'],
          },
        ],
      }),
      {
        path: '/settings/',
        title: t('Settings'),
        icon: <SettingsIcon />,
        permissions: [
          'company:general:write:all',
          'department:general:write:department',
          'department:general:write:all',
          'announcement:general:write:department',
          'announcement:general:write:all',
          'notification:general:read:all',
          'employee:general:write:all',
          'form_field:general:read:all',
          'translation:general:read:all',
          'permission:general:write:all',
          'external_integrations:general:write:all',
          'superadmin',
        ],
        subMenuItems: [
          {
            to: 'company/*',
            label: t('Company'),
            permissions: ['company:general:read:all'],
          },
          {
            to: 'departments/*',
            label: t('Departments'),
            permissions: [
              'department:general:read:all',
              'department:general:read:department',
            ],
          },
          ...insertIf(!!hasNativeAppAccess, {
            to: 'events/*',
            label: t('Events'),
            permissions: ['event:general:read:all'],
          }),
          {
            to: 'announcements/*',
            label: t('Announcements'),
            permissions: [
              'announcement:general:write:department',
              'announcement:general:write:all',
            ],
          },
          {
            to: 'notifications/*',
            label: t('Notifications'),
            permissions: ['notification:general:read:all'],
          },
          {
            to: 'employees',
            label: t('Manage employees'),
            permissions: ['employee:general:write:all'],
          },
          {
            to: 'personal-data-card/*',
            label: t('Personal data configuration'),
            permissions: ['form_field:general:read:all'],
          },
          {
            to: 'permissions/*',
            label: t('Permissions'),
            permissions: ['permission:general:write:all'],
          },
          {
            to: 'roles/*',
            label: t('Roles'),
            permissions: ['permission:general:write:all'],
          },
          {
            to: 'widgets/*',
            label: t('Personalised widgets'),
            permissions: ['dashboard:general:read:all'],
          },
          {
            to: 'translations',
            label: t('Translations'),
            permissions: ['translation:general:read:all'],
          },
          ...insertIf(!!hasNativeAppAccess, {
            to: 'message-boards/*',
            label: t('Message boards'),
            permissions: ['post:type:write:all'],
          }),
          {
            to: 'externalIntegration/*',
            label: t('External Integration'),
            permissions: ['external_integrations:general:write:all'],
          },
          {
            to: 'asyncjobs',
            label: t('Jobs'),
            permissions: ['godmode'],
          },
          {
            to: 'companies/*',
            label: t('Companies'),
            permissions: ['superadmin'],
          },
          {
            to: 'changelog/*',
            label: t('Changelog'),
            permissions: ['godmode'],
          },
          {
            to: 'roadmap',
            label: t('Roadmap'),
            permissions: ['godmode'],
          },
          {
            to: 'tools',
            label: t('Tools'),
            permissions: ['godmode'],
          },
        ],
      },
      {
        path: '/information/',
        title: t('Information'),
        icon: <InformationIcon />,
        permissions: [
          'general:information_button:read:self',
          'general:information_button:read:department',
          'general:information_button:read:all',
        ],
        subMenuItems: [
          {
            to: '',
            label: t('Information'),
          },
          {
            to: 'release-notes',
            label: t('Release notes'),
          },
          {
            to: 'roadmap',
            label: t('Roadmap'),
          },
        ],
      },
      {
        path: 'https://hr-skyen.dk/hr',
        title: t('HR-ON Recruit'),
        icon: <RecruitIcon />,
        permissions: [
          'general:recruitment_button:read:self',
          'general:recruitment_button:read:department',
          'general:recruitment_button:read:all',
        ],
      },
    ] as MenuItem[]
  }, [company?.nativeAppAccess, timeRegistrationEnabled, dashboardCount])
}
